import { KeyboardEventHandler, MouseEventHandler, PointerEventHandler } from "react";
import { useSplitContent } from "@/app/features/shared/split-content/contexts/SplitContentProvider";
import clsx from "clsx";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Image from "next/image";
import styles from "./split-content.module.css";

function getDragHandleClassName(orientation: Orientation) {
    switch (orientation) {
        case 'row': return styles.dragHandleRow;
        case 'row-reverse': return styles.dragHandleRowReverse;
        case 'column': return styles.dragHandleColumn;
        case 'column-reverse': return styles.dragHandleColumnReverse;
    }
}

export default function DragHandle({
    splitPosition,
    maxSplitPosition,
    orientation,
    onPointerDown,
    onClick,
    animate,
}: {
    splitPosition: number,
    maxSplitPosition: number,
    orientation: Orientation,
    onPointerDown: PointerEventHandler<HTMLDivElement>,
    onClick: MouseEventHandler<HTMLDivElement>,
    animate?: boolean,
}) {

    const { isLeading, isHorizontal } = useSplitContent();
    const splitPositionMin = isLeading ? 0 : 100 - maxSplitPosition;
    const splitPositionMax = isLeading ? maxSplitPosition : 100;

    const canGoForward = splitPosition < splitPositionMax - 0.1;
    const canGoBackward = splitPosition > splitPositionMin + 0.1;

    const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.currentTarget.click();
        }
    };

    return (
        <Box
            className={clsx(
                styles.dragHandle,
                getDragHandleClassName(orientation),
                animate && styles.dragHandleAnimated,
            )}
            sx={{
                "--split-position": `${splitPosition}%`,
            }}
            role="button"
            tabIndex={0}
            aria-label="Split-Position umschalten"
            onPointerDown={onPointerDown}
            onClick={onClick}
            onKeyDown={handleKeyDown}
        >
            <Grid
                className={clsx(
                    "flex h-auto w-full m-2 place-self-center",
                    !isHorizontal && "rotate-90",
                )}
                container
                columns={2}
                spacing={1}
            >
                <Grid size={1} className="place-items-end">
                    {canGoBackward && (
                        <Image
                            src="/assets/drag-handle-left.png"
                            alt=""
                            width={24}
                            height={24}
                            draggable={false}
                            aria-hidden
                        />
                    )}
                </Grid>
                <Grid size={1}>
                    {canGoForward && (
                        <Image
                            className="rotate-180"
                            src="/assets/drag-handle-left.png"
                            alt=""
                            width={24}
                            height={24}
                            draggable={false}
                            aria-hidden
                        />
                    )}
                </Grid>
            </Grid>
        </Box>
    )
}
